import { createAsyncThunk } from '@reduxjs/toolkit';
import * as R from 'ramda';

import {
  ApiListenRejectedReason,
  ComplianceTypes,
  ListenRejectedReasons,
  Modals,
  PlaybackErrors,
  PLAYER_POSITION_REGEX,
} from '@/constants';
import {
  AnalyticsService,
  EVENT_CONSTANTS,
  EVENTS,
  growthbook,
  log,
  logBreadcrumb,
} from '@/services';
import { Referrer } from '@/types';
import { isTestModeEnabled, SnackbarUtils } from '@/utils';

import { chaptersApi } from '../chapters-api';
import { episodesApi } from '../episodes-api';
import { PlayerSessionTransitionResponse } from '../player-fsm/player-fsm.types';
import {
  playerFsmApi,
  selectEpisodeCompliance,
  selectUserCompliance,
} from '../player-fsm-api';
import { postgrestApi } from '../postgrest-api';
import { selectSessionUserId } from '../session';
import { RootState } from '../store';
import { PostgrestApiTags } from '../store.constants';
import { maybeShowKeepGoingPromo, selectActiveModal, uiActions } from '../ui';
import { selectPlan } from '../user-api';
import { selectChapterProgress } from '../user-chapters-api';
import { PLAYER_SLICE_KEY } from './player.constants';
import {
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectActiveSessionId,
  selectIsChangingChapter,
  selectIsPlayerPlaying,
  selectIsPlayerVideoFullscreen,
  selectIsSavingProgress,
  selectLastSavedProgress,
  selectPlayerAutoSaveInterval,
  selectPlayerLocationReferrer,
  selectPlayerProgress,
} from './player.selectors';
import { playerActions } from './player.slice';
import {
  ChangeActiveChapterThunkPayload,
  ChangeActiveChapterThunkResolveValue,
  ChapterPlayPressedThunkPayload,
  EpisodePlayPressedThunkPayload,
  PlayerThunkWithListenRejectReason,
  RejectWithReason,
} from './player.types';
import { getListenRejectReason, isChapterCompleted } from './player.utils';

export const playPlayer = createAsyncThunk<void, void, { state: RootState }>(
  `${PLAYER_SLICE_KEY}/play`,
  async (_, { dispatch, getState }) => {
    const sessionId = selectActiveSessionId(getState());
    const chapterId = selectActiveChapterId(getState());
    const isPlayerPlaying = selectIsPlayerPlaying(getState());
    const position = selectPlayerProgress(getState())?.position;

    if (!isPlayerPlaying && sessionId && chapterId) {
      // If this fails we should avoid adjusting playback state
      await dispatch(
        playerFsmApi.endpoints.playPlayerSession.initiate({
          sessionId,
          chapterId,
          position,
        }),
      ).unwrap();
    }
  },
);

export const pausePlayer = createAsyncThunk<void, void, { state: RootState }>(
  `${PLAYER_SLICE_KEY}/pause`,
  async (_, { dispatch, getState }) => {
    const sessionId = selectActiveSessionId(getState());
    const chapterId = selectActiveChapterId(getState());
    const position = selectPlayerProgress(getState()).position;
    const isPlayerPlaying = selectIsPlayerPlaying(getState());

    if (isPlayerPlaying && sessionId && chapterId && !R.isNil(position)) {
      // Even if this fails, it should not block pausing the local player state
      dispatch(
        playerFsmApi.endpoints.pausePlayerSession.initiate({
          sessionId,
          chapterId,
          position,
        }),
      );
    }
  },
);

const resumeLastSession = createAsyncThunk<
  ChangeActiveChapterThunkResolveValue | void,
  void,
  {
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/resumeLastSession`,
  async (_, { dispatch, getState }) => {
    const userId = selectSessionUserId(getState());
    if (!userId) return;

    const lastPlayerSession = await dispatch(
      playerFsmApi.endpoints.resumeLastPlayerSession.initiate(),
    ).unwrap();

    if (!lastPlayerSession) return;

    const {
      clientDirectives,
      sessionId,
      state: { chapters, episodeId, currentPosition, state },
    } = lastPlayerSession;

    if (!currentPosition) return;

    dispatch(
      playerActions.setPlaybackSpeedOptions(clientDirectives.playbackSpeeds),
    );
    dispatch(
      playerActions.setAutoSaveInterval(clientDirectives.saveIntervalDelaySec),
    );

    const firstChapter = chapters.find(c => !c.isTrailer);
    const targetChapter =
      chapters.find(({ id }) => id === currentPosition.chapterId) ||
      firstChapter;

    if (!targetChapter) {
      throw new Error('no suitable chapter found');
    }

    // Don't resume trailers or episodes that are already completed
    if (targetChapter.isTrailer || ['completed', 'verified'].includes(state))
      return;

    const newChapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        {
          chapterId: targetChapter.id,
          compliance: selectEpisodeCompliance(getState(), episodeId),
        },
        { subscribe: false },
      ),
    ).unwrap();

    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate(
        { episodeId, compliance: selectUserCompliance(getState()) },
        { subscribe: false },
      ),
    ).unwrap();

    if (!newChapter) {
      throw new Error('chapter not found');
    }

    if (!episode) {
      throw new Error('episode not found');
    }

    const isNewChapterVideo = newChapter.type === 'video';
    const isVideoFullscreen = selectIsPlayerVideoFullscreen(getState());

    dispatch(
      setMediaSessionData({
        title: newChapter.name,
        artist: 'LumiQ',
        album: episode?.name,
        artwork: [
          {
            src: episode.cover,
            type: 'image/jpeg',
          },
        ],
      }),
    );

    return {
      activeChapter: {
        chapterId: targetChapter.id,
        episodeId,
        sessionId,
      },
      autoPlay: false,
      isExpanded: false,
      isVideoFullscreen: isVideoFullscreen && isNewChapterVideo,
      seekToPosition: targetChapter.progress.currentTime,
    };
  },
);

const chapterPlayPressed = createAsyncThunk<
  void,
  ChapterPlayPressedThunkPayload,
  {
    rejectValue: PlayerThunkWithListenRejectReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/chapterPlayPressed`,
  async (payload, { getState, dispatch }) => {
    const { activeChapter, location, referrer } = payload;
    const playerChapterId = selectActiveChapterId(getState());
    const isChapterActive = playerChapterId === activeChapter.chapterId;

    const { chapterId, episodeId } = activeChapter;
    const compliance = selectEpisodeCompliance(getState(), episodeId);

    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate(
        { episodeId, compliance },
        { subscribe: false },
      ),
    ).unwrap();
    const chapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        { chapterId, compliance },
        { subscribe: false },
      ),
    ).unwrap();

    if (!chapter) {
      throw new Error('chapter not found');
    }

    if (!episode) {
      throw new Error('episode not found');
    }

    const userId = selectSessionUserId(getState());
    if (!userId && !chapter.isTrailer) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: { episodeId, referrer: location },
        }),
      );
      return;
    }

    if (isChapterActive) {
      const isPlayerPlaying = selectIsPlayerPlaying(getState());
      dispatch(isPlayerPlaying ? pausePlayer() : playPlayer());

      AnalyticsService.chapterPause({
        chapterId,
        episodeId,
        location,
        referrer,
        pausing: isPlayerPlaying,
      });
    } else {
      try {
        await dispatch(changeActiveChapter(payload)).unwrap();
      } catch (error: any) {
        if (error.reason) {
          dispatch(
            handleListenRejected({
              reason: error.reason,
              episodeId: activeChapter.episodeId,
            }),
          );
        } else {
          throw error;
        }
      }
    }
  },
);

const episodePlayPressed = createAsyncThunk<
  void,
  EpisodePlayPressedThunkPayload,
  {
    rejectValue: PlayerThunkWithListenRejectReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/episodePlayPressed`,
  async (payload, { getState, dispatch }) => {
    const { episodeId, location, referrer } = payload;

    const userId = selectSessionUserId(getState());
    if (!userId) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: { episodeId, referrer: location },
        }),
      );
      return;
    }

    const compliance = selectEpisodeCompliance(getState(), episodeId);
    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate(
        {
          episodeId,
          compliance,
          includeArchived: true,
        },
        { subscribe: false },
      ),
    ).unwrap();

    if (!episode) {
      throw new Error('episode not found');
    }

    if (episode.isArchived) {
      SnackbarUtils.warning('Episode has been archived');
      throw new Error('Episode has been archived');
    }

    const playerChapterId = selectActiveChapterId(getState());
    const playerEpisodeId = selectActiveEpisodeId(getState());
    const isEpisodeActive = playerEpisodeId === episodeId;
    const isTrailerActive = episode.trailerId === playerChapterId;
    const chapter = playerChapterId
      ? await dispatch(
          chaptersApi.endpoints.fetchChapter.initiate(
            {
              chapterId: playerChapterId,
              compliance,
            },
            {
              subscribe: false,
            },
          ),
        ).unwrap()
      : null;

    if (isEpisodeActive && !isTrailerActive) {
      const isPlayerPlaying = selectIsPlayerPlaying(getState());
      dispatch(isPlayerPlaying ? pausePlayer() : playPlayer());

      if (chapter) {
        AnalyticsService.chapterPause({
          chapterId: chapter.chapterId,
          episodeId,
          location,
          referrer,
          pausing: isPlayerPlaying,
        });
      }
    } else {
      try {
        await dispatch(
          changeActiveChapter({
            ...payload,
            activeChapter: { episodeId },
          }),
        ).unwrap();
      } catch (error: any) {
        if (error.reason) {
          dispatch(
            handleListenRejected({
              reason: error.reason,
              episodeId,
            }),
          );
          return;
        }

        throw error;
      }
    }
  },
);

const changeActiveChapter = createAsyncThunk<
  ChangeActiveChapterThunkResolveValue,
  ChangeActiveChapterThunkPayload,
  {
    rejectValue: RejectWithReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/changeActiveChapter`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const { activeChapter, location, referrer } = payload;
    const { episodeId } = activeChapter;

    const isPlayerPlaying = selectIsPlayerPlaying(getState());
    const currentChapterId = selectActiveChapterId(getState());

    if (currentChapterId && isPlayerPlaying) {
      dispatch(pausePlayer());
    }

    const userId = selectSessionUserId(getState());
    if (!userId && activeChapter.chapterId) {
      // Allow listening to trailers without being logged in
      const compliance = selectUserCompliance(getState());

      const chapter = await dispatch(
        chaptersApi.endpoints.fetchChapter.initiate(
          { chapterId: activeChapter.chapterId, compliance },
          { subscribe: false },
        ),
      ).unwrap();
      const episode = await dispatch(
        episodesApi.endpoints.fetchEpisode.initiate(
          { episodeId, compliance },
          { subscribe: false },
        ),
      ).unwrap();

      if (chapter?.isTrailer) {
        AnalyticsService.chapterPlay({
          chapterId: activeChapter.chapterId,
          episodeId,
          location,
          referrer,
        });
        AnalyticsService.logEvent(EVENTS.MARKETING_EVENTS.PLAY_TRAILER);
        dispatch(
          setMediaSessionData({
            title: 'Trailer',
            artist: 'LumiQ',
            album: episode?.name,
            ...(episode?.cover
              ? {
                  artwork: [
                    {
                      src: episode.cover,
                      type: 'image/jpeg',
                    },
                  ],
                }
              : {}),
          }),
        );
        return {
          ...payload,
          isExpanded: false,
          isVideoFullscreen: false,
          seekToPosition: 0,
        };
      }
    }

    const {
      clientDirectives,
      sessionId: activeSessionId,
      state: { chapters, currentPosition, state },
    } = await dispatch(
      playerFsmApi.endpoints.startPlayerSession.initiate({
        episodeId,
      }),
    ).unwrap();

    const lastChapterListened = currentPosition?.chapterId;
    const isLastChapterListenedTrailer = chapters.find(
      c => c.id === lastChapterListened,
    )?.isTrailer;
    const firstNonTrailerChapter = chapters.find(c => !c.isTrailer)?.id;

    // If last listening was at the end of the final chapter, restart from the start of the first chapter
    const finalChapter = chapters[chapters.length - 1];
    const isLandingZone =
      lastChapterListened === finalChapter.id &&
      ['completed', 'verified'].includes(state) &&
      isChapterCompleted({
        currentTime: currentPosition!.position,
        duration: finalChapter.progress.playableDuration,
      });

    const chapterId =
      activeChapter.chapterId ||
      (!isLastChapterListenedTrailer &&
        !isLandingZone &&
        lastChapterListened) ||
      firstNonTrailerChapter;

    if (!chapterId)
      throw new Error('No verifiable chapter ID provided or determined');

    dispatch(
      playerActions.setPlaybackSpeedOptions(clientDirectives.playbackSpeeds),
    );
    dispatch(
      playerActions.setAutoSaveInterval(clientDirectives.saveIntervalDelaySec),
    );

    try {
      await dispatch(
        playerFsmApi.endpoints.playPlayerSession.initiate({
          sessionId: activeSessionId,
          chapterId,
        }),
      ).unwrap();
    } catch (error: any) {
      if (error.status === 401) {
        return rejectWithValue({
          reason: ListenRejectedReasons.NOT_LOGGED_IN,
          chapterId,
          episodeId,
        });
      }

      if (error.data?.message === PlaybackErrors.PLAY_NOT_AUTHORIZED) {
        return rejectWithValue({
          reason: error.data?.details,
          chapterId,
          episodeId,
        });
      }

      throw error;
    }

    const compliance = selectEpisodeCompliance(getState(), episodeId);
    const newChapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        { chapterId, compliance },
        { subscribe: false },
      ),
    ).unwrap();
    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate(
        { episodeId, compliance },
        { subscribe: false },
      ),
    ).unwrap();

    if (!newChapter) {
      throw new Error('chapter not found');
    }

    if (!episode) {
      throw new Error('episode not found');
    }

    const isNewChapterVideo = newChapter.type === 'video';
    const isVideoFullscreen = selectIsPlayerVideoFullscreen(getState());

    const chapterProgress = chapters.find(({ id }) => id === chapterId);

    if (!chapterProgress) {
      throw new Error('Chapter progress not found');
    }

    const { currentTime } = chapterProgress.progress;
    const { duration } = newChapter;

    const isLastUserProgressAtEndOfChapter =
      isLandingZone ||
      isChapterCompleted({
        currentTime,
        duration,
      });

    const seekToPosition = isLastUserProgressAtEndOfChapter ? 0 : currentTime;

    if (seekToPosition === 0 && !newChapter.isTrailer) {
      await dispatch(
        playerFsmApi.endpoints.seekPosition.initiate({
          sessionId: activeSessionId,
          chapterId,
          position: -0,
        }),
      ).unwrap();
    }

    // the fsm player will handle the majority of analytics events via savePlayerProgress
    AnalyticsService.chapterPlay({
      chapterId,
      episodeId,
      location,
      referrer,
    });

    dispatch(
      setMediaSessionData({
        title: newChapter.isTrailer ? 'Trailer' : newChapter.name,
        artist: 'LumiQ',
        album: episode?.name,
        artwork: [
          {
            src: episode.cover,
            type: 'image/jpeg',
          },
        ],
      }),
    );

    const autoExpandVisualAidEnabled = growthbook.getFeatureValue(
      'visual-aid-auto-expand',
      false,
    );
    const hasVisualAid = !!episode.visualAid;
    const autoExpandeVisualAid =
      autoExpandVisualAidEnabled && hasVisualAid && !newChapter.isTrailer;

    if (autoExpandeVisualAid) {
      AnalyticsService.logEvent(
        EVENTS.VISUAL_LEARNING_EVENTS.OPEN_VISUAL_LEARNING,
        {
          episodeId,
          actor: 'player',
        },
      );
    }

    return {
      ...payload,
      activeChapter: {
        chapterId,
        episodeId,
        sessionId: activeSessionId,
      },
      isExpanded: isNewChapterVideo || autoExpandeVisualAid,
      isVideoFullscreen: isVideoFullscreen && isNewChapterVideo,
      seekToPosition,
    };
  },
);

const seekToPosition = createAsyncThunk<
  void,
  { position?: number; delta?: number },
  {
    rejectValue: RejectWithReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/seekToPosition`,
  async (payload, { dispatch, getState }) => {
    const playerProgress = selectPlayerProgress(getState());
    const activeChapterId = selectActiveChapterId(getState());
    const activeEpisodeId = selectActiveEpisodeId(getState());
    const activeSessionId = selectActiveSessionId(getState());

    if (
      activeChapterId === undefined ||
      activeEpisodeId === undefined ||
      playerProgress.position === undefined
    ) {
      logBreadcrumb({
        message: 'seekToPosition',
        data: {
          activeChapterId,
          activeEpisodeId,
          playerProgress,
        },
      });

      throw new Error(
        'activeChapterId, activeEpisodeId, or playerProgress not set',
      );
    }

    const compliance = selectEpisodeCompliance(getState(), activeEpisodeId);

    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate({
        episodeId: activeEpisodeId,
        compliance,
      }),
    ).unwrap();

    if (!episode) {
      throw new Error('episode not found');
    }

    const chapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        {
          chapterId: activeChapterId,
          compliance,
        },
        { subscribe: false },
      ),
    ).unwrap();

    if (!chapter) {
      throw new Error('chapter not found');
    }

    const userChapter = selectChapterProgress(getState(), {
      episodeId: activeEpisodeId,
      chapterId: activeChapterId,
    });

    const chapterProgress = userChapter?.progress;

    const isInTestMode = isTestModeEnabled();
    const isCompleted = isChapterCompleted({
      currentTime: chapterProgress?.playedDuration || 0,
      duration: chapter.duration,
    });

    // payload is either the desired position or the delta from current position
    const desiredPosition =
      payload.position !== undefined
        ? payload.position
        : playerProgress.position + (payload.delta ?? 0);

    const minSeekPosition = 1;
    let seekToPosition = Math.max(desiredPosition, minSeekPosition);

    if (seekToPosition !== playerProgress.position) {
      try {
        if (chapter.isTrailer) {
          dispatch(playerActions.setSeekToPosition(seekToPosition));
          return;
        }

        if (!activeSessionId) {
          throw new Error('activeSessionId not found');
        }

        await dispatch(
          playerFsmApi.endpoints.seekPosition.initiate({
            sessionId: activeSessionId,
            chapterId: activeChapterId,
            position:
              // the api expects a negative number if seeking backwards
              // but the number does not represent the delta,
              // it represents the final desired position
              seekToPosition > playerProgress.position
                ? seekToPosition
                : -seekToPosition,
            isInTestMode,
          }),
        ).unwrap();
        dispatch(playerActions.setSeekToPosition(seekToPosition));
      } catch (error: any) {
        if (error?.data?.details?.includes('seek.invalid')) {
          const maxSeekPosition = parseInt(
            error?.data?.message?.match(PLAYER_POSITION_REGEX)?.[1],
          );
          seekToPosition = maxSeekPosition ?? playerProgress.position;

          const message =
            compliance === ComplianceTypes.CPD
              ? "Skipping ahead is only available after you've listened."
              : 'Your organization requires you to listen before skipping ahead is allowed.';
          SnackbarUtils.info(message);

          if (seekToPosition > playerProgress.position) {
            // Seek to the furthest point allowed
            dispatch(playerActions.setSeekToPosition(seekToPosition));
          }
        } else {
          throw error;
        }
      }

      AnalyticsService.chapterSeek({
        chapterId: activeChapterId,
        episodeId: activeEpisodeId,
        type: chapter.type || 'audio',
        seekStart: playerProgress.position,
        seekLength: seekToPosition - playerProgress.position,
        alreadyListened: isCompleted,
      });
    }
  },
);

const updatePlayerProgress = createAsyncThunk<
  void,
  {
    progress: { position: number; buffered?: TimeRanges };
  },
  {
    rejectValue: RejectWithReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/updatePlayerProgress`,
  async (payload, { dispatch, getState }) => {
    const {
      progress: { position },
    } = payload;

    const isChangingChapter = selectIsChangingChapter(getState());
    const isSavingProgress = selectIsSavingProgress(getState());
    const lastSavedProgress = selectLastSavedProgress(getState());
    const autoSaveInterval = selectPlayerAutoSaveInterval(getState());
    const progressSinceLastSave = position - lastSavedProgress;
    const activeEpisodeId = selectActiveEpisodeId(getState());
    const activeChapterId = selectActiveChapterId(getState());

    if (!activeChapterId || !activeEpisodeId) {
      throw new Error('activeChapterId or activeEpisodeId not found');
    }

    const chapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        {
          chapterId: activeChapterId,
          compliance: selectEpisodeCompliance(getState(), activeEpisodeId),
        },
        { subscribe: false },
      ),
    ).unwrap();

    if (!chapter) {
      throw new Error('chapter not found');
    }

    const { isTrailer } = chapter;

    if (
      !isChangingChapter &&
      !isSavingProgress &&
      !isTrailer &&
      progressSinceLastSave >= autoSaveInterval
    ) {
      const sessionId = selectActiveSessionId(getState());
      if (sessionId) {
        await dispatch(
          savePlayerProgress({
            chapterId: activeChapterId,
            position,
            sessionId,
          }),
        );
      }
    }
  },
);

const savePlayerProgress = createAsyncThunk<
  { position?: number },
  {
    chapterId: string;
    position: number;
    sessionId: string;
  },
  {
    rejectValue: RejectWithReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/savePlayerProgress`,
  async (payload, { dispatch, getState }) => {
    const { chapterId, position, sessionId } = payload;

    const userId = selectSessionUserId(getState());
    if (!userId) return { position: undefined };
    const activeEpisodeId = selectActiveEpisodeId(getState());

    if (!activeEpisodeId) {
      throw new Error('activeEpisodeId not found');
    }

    const chapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        {
          chapterId,
          compliance: selectEpisodeCompliance(getState(), activeEpisodeId),
        },
        { subscribe: false },
      ),
    ).unwrap();

    if (!chapter) {
      throw new Error('chapter not found');
    }

    try {
      const setPositionResponse = await dispatch(
        playerFsmApi.endpoints.setPosition.initiate({
          sessionId,
          chapterId,
          position,
        }),
      ).unwrap();

      const {
        state: { transitions },
      } = setPositionResponse;

      if (transitions.length) {
        await dispatch(
          logAnalyticsEventsFsm({ chapterId, setPositionResponse }),
        );

        dispatch(
          postgrestApi.util.invalidateTags([
            PostgrestApiTags.PLAYER_SESSIONS,
            PostgrestApiTags.PLAN,
          ]),
        );
      }

      return { position };
    } catch (error: any) {
      await dispatch(pausePlayer());
      if (error?.data?.details === PlaybackErrors.CONCURRENT_SESSION) {
        const activeModal = selectActiveModal(getState());
        if (activeModal?.name !== Modals.CONCURRENT_SESSION_PROHIBITED) {
          dispatch(
            uiActions.setActiveModal({
              name: Modals.CONCURRENT_SESSION_PROHIBITED,
            }),
          );
        }
      } else {
        log(new Error('Error saving player progress'), error, payload);
        SnackbarUtils.error(
          'There was an issue saving your progress. Please try again later.',
        );
      }
      throw error;
    }
  },
);

const chapterEnded = createAsyncThunk<
  void,
  void,
  {
    rejectValue: RejectWithReason;
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/chapterEnded`,
  async (payload, { dispatch, getState }) => {
    const activeChapterId = selectActiveChapterId(getState());
    const activeEpisodeId = selectActiveEpisodeId(getState());
    const userId = selectSessionUserId(getState());

    if (!activeChapterId || !activeEpisodeId) {
      throw new Error('activeChapterId or activeEpisodeId not set');
    }

    const compliance = selectEpisodeCompliance(getState(), activeEpisodeId);

    const chapter = await dispatch(
      chaptersApi.endpoints.fetchChapter.initiate(
        {
          chapterId: activeChapterId,
          compliance,
        },
        { subscribe: false },
      ),
    ).unwrap();

    const chapters = await dispatch(
      chaptersApi.endpoints.fetchEpisodeChapters.initiate(
        {
          episodeId: activeEpisodeId,
          compliance,
        },
        { subscribe: false },
      ),
    ).unwrap();

    if (!chapter || !chapters) {
      throw new Error('chapter not found');
    }

    AnalyticsService.chapterComplete({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
    });

    if (chapter.isTrailer && !userId) {
      dispatch(playerActions.clearActiveChapter());
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: {
            episodeId: activeEpisodeId,
            referrer: { details: EVENT_CONSTANTS.PREVIEW_END },
          },
        }),
      );
      return;
    }

    const chapterPosition = chapters?.find(
      ({ chapterId }) => chapterId === activeChapterId,
    )?.chapterPosition;

    if (chapterPosition === undefined) {
      throw new Error('chapter position not found');
    }

    const nextChapter = chapters?.find(
      c => c.chapterPosition === chapterPosition + 1,
    );

    if (nextChapter && !chapter.isTrailer) {
      try {
        await dispatch(
          changeActiveChapter({
            activeChapter: {
              episodeId: nextChapter.episodeId,
              chapterId: nextChapter.chapterId,
            },
            location: {
              button: EVENT_CONSTANTS.AUTO_PLAY,
            },
          }),
        ).unwrap();
      } catch (error: any) {
        if (error.reason) {
          dispatch(
            handleListenRejected({
              reason: error.reason,
              episodeId: activeEpisodeId,
            }),
          );
        } else {
          throw error;
        }
      }
    } else {
      dispatch(playerActions.clearActiveChapter());
    }

    if (!chapter.isTrailer) dispatch(maybeShowKeepGoingPromo());
  },
);

const handleListenRejected = createAsyncThunk<
  void,
  {
    reason: ApiListenRejectedReason;
    episodeId: string;
  },
  {
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/handleListenRejected`,
  async (payload, { dispatch, getState }) => {
    const { reason: apiReason, episodeId } = payload;
    const compliance = selectEpisodeCompliance(getState(), episodeId);
    const { data: plan } = selectPlan(getState());

    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate(
        { episodeId, compliance },
        { subscribe: false },
      ),
    ).unwrap();

    const reason = getListenRejectReason({
      apiReason,
      episode,
      plan,
      compliance,
    });

    if (reason === ListenRejectedReasons.NOT_LOGGED_IN) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: { episodeId },
        }),
      );
    } else if (reason === ListenRejectedReasons.NEED_REDEEM) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.REDEEM,
          params: {
            episodeId,
          },
        }),
      );
    } else {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.LISTEN_REJECTED,
          params: {
            listenRejectedReason: reason,
            episodeId,
          },
        }),
      );
    }
  },
);

const logAnalyticsEventsFsm = createAsyncThunk<
  void,
  {
    chapterId: string;
    setPositionResponse: PlayerSessionTransitionResponse;
  },
  {
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/logAnalyticsEventsFsm`,
  async (payload, { getState, dispatch }) => {
    const { chapterId, setPositionResponse } = payload;
    const {
      state: { newState, transitions },
    } = setPositionResponse;

    if (transitions.length) {
      const { referrer, location } = selectPlayerLocationReferrer(getState());

      const isEpisodeListenCompleted = newState.chapters.every(
        c => c.completed || !c.isVerifiable,
      );
      const chapterStartedTransition = transitions.find(
        t => t.stateType === 'chapter' && t.oldState === 'ready',
      );
      const episodeStartedTransition = transitions.find(
        t => t.stateType === 'session' && t.oldState === 'ready',
      );

      if (chapterStartedTransition) {
        AnalyticsService.chapterStart({
          chapterId,
          episodeId: newState.episodeId,
          location,
          referrer,
        });
      }

      if (episodeStartedTransition) {
        const episode = await dispatch(
          episodesApi.endpoints.fetchEpisode.initiate(
            {
              episodeId: newState.episodeId,
              compliance:
                newState.fsmType === 'cpd_fsm'
                  ? ComplianceTypes.CPD
                  : ComplianceTypes.CPE,
            },
            {
              subscribe: false,
            },
          ),
        ).unwrap();
        AnalyticsService.episodeStart({
          chapterId,
          episodeId: newState.episodeId,
          location,
          referrer,
          hasTranscript:
            episode?.resources.some(r =>
              r.name.toLowerCase().includes('transcript'),
            ) || false,
          hasVisualAid: !!episode?.visualAid,
        });
      }

      if (isEpisodeListenCompleted) {
        AnalyticsService.episodeListened({
          episodeId: newState.episodeId,
        });

        dispatch(
          uiActions.setActiveModal({
            name: Modals.EPISODE_FEEDBACK,
            params: { episodeId: newState.episodeId, chapterId },
          }),
        );
      }
    }
  },
);

export const setMediaSessionData = createAsyncThunk<
  void,
  {
    title: string;
    artist: string;
    album?: string;
    artwork?: { src: string; type: string }[];
  },
  {
    state: RootState;
  }
>(`${PLAYER_SLICE_KEY}/setMediaSessionData`, async (payload, { dispatch }) => {
  if ('mediaSession' in navigator) {
    const { title, artist, album, artwork } = payload;
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist,
      album,
      artwork,
    });
    navigator.mediaSession.setActionHandler('seekto', async seek => {
      const position = seek.seekTime;
      dispatch(seekToPosition({ position }));
    });
    navigator.mediaSession.setActionHandler('play', () => {
      dispatch(playPlayer());
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      dispatch(pausePlayer());
    });
  }
});

const initiateRedemption = createAsyncThunk<
  void,
  { episodeId: string; location?: Referrer },
  {
    state: RootState;
  }
>(
  `${PLAYER_SLICE_KEY}/initiateRedemption`,
  async (payload, { dispatch, getState }) => {
    const { episodeId } = payload;

    const userId = selectSessionUserId(getState());
    const { data: plan } = selectPlan(getState());

    if (!userId) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SIGN_UP,
          params: { episodeId, referrer: payload.location || '' },
        }),
      );
      return;
    }

    const episode = await dispatch(
      episodesApi.endpoints.fetchEpisode.initiate(
        { episodeId, compliance: ComplianceTypes.CPE },
        { subscribe: false },
      ),
    ).unwrap();

    if (!episode || !plan) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.LISTEN_REJECTED,
          params: {
            listenRejectedReason: ListenRejectedReasons.UNKNOWN,
            episodeId,
          },
        }),
      );
      return;
    }

    // Handle expired time plans
    if (plan.validUntil && new Date(plan.validUntil) < new Date()) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.LISTEN_REJECTED,
          params: {
            listenRejectedReason: ListenRejectedReasons.PLAN_EXPIRED,
            episodeId,
          },
        }),
      );
      return;
    }

    // Handle depleted usage plans as a fallback
    if (!plan.licenceValid) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.LISTEN_REJECTED,
          params: {
            listenRejectedReason: ListenRejectedReasons.PLAN_USED_UP,
            episodeId,
          },
        }),
      );
      return;
    }

    dispatch(
      uiActions.setActiveModal({ name: Modals.REDEEM, params: payload }),
    );
  },
);

export const playerThunks = {
  resumeLastSession,
  episodePlayPressed,
  chapterPlayPressed,
  changeActiveChapter,
  seekToPosition,
  updatePlayerProgress,
  savePlayerProgress,
  chapterEnded,
  playPlayer,
  pausePlayer,
  initiateRedemption,
};
